// Excluir produto (dono, 26/10/2026): sem histórico apaga de vez; com venda,
// mesa aberta, produção ou promoção vai para Arquivados. O popup pergunta antes
// e diz qual dos dois vai acontecer.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Loader2, Trash2 } from "lucide-react";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { excluirProduto, mensagemDeErro, previaProduto } from "./api";

export default function ExcluirProdutoDialog({ produto, aoFechar, aoExcluir }) {
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    setPrevia(null); setErro("");
    if (!produto) return;
    let vivo = true;
    previaProduto(produto.id)
      .then((p) => vivo && setPrevia(p))
      .catch((e) => vivo && setErro(mensagemDeErro(e, "Não foi possível verificar o produto")));
    return () => { vivo = false; };
  }, [produto]);

  const apagar = previa?.acao === "apagar";

  const confirmar = async (e) => {
    e.preventDefault();
    if (!previa || enviando) return;
    setEnviando(true);
    try {
      const r = await excluirProduto(produto.id);
      toast.success(r.acao === "apagado" ? `"${produto.name}" apagado` : `"${produto.name}" foi para Arquivados`);
      aoExcluir(produto.id, r.acao);
      aoFechar();
    } catch (err) {
      toast.error(mensagemDeErro(err, "Erro ao excluir produto"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <AlertDialog open={!!produto} onOpenChange={(v) => { if (!v) aoFechar(); }}>
      <AlertDialogContent data-e2e="excluir-produto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${previa && !apagar ? "bg-amber-500/15" : "bg-red-500/15"}`}>
              {previa && !apagar
                ? <Archive className="w-5 h-5 text-amber-500" />
                : <Trash2 className="w-5 h-5 text-red-500" />}
            </div>
            {!previa ? "Excluir produto" : apagar ? "Apagar produto de vez?" : "Arquivar produto"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 pt-2 text-sm">
              {!previa && !erro && (
                <p className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verificando se o produto tem vendas…</p>
              )}
              {erro && <p className="text-red-500">{erro}</p>}
              {previa && apagar && (
                <>
                  <p>
                    <strong className="text-foreground">{produto?.name}</strong> nunca foi vendido. Ele será
                    apagado do cardápio e do sistema, e sai também dos combos onde aparece como opção.
                  </p>
                  <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-foreground/80">
                    <strong className="text-red-500">Não tem volta:</strong> o produto não vai para Arquivados e não pode ser recuperado.
                  </p>
                </>
              )}
              {previa && !apagar && (
                <p>
                  <strong className="text-foreground">{produto?.name}</strong> {previa.motivo}, por isso não pode ser
                  apagado (mudaria relatórios e faturamento). Ele vai para <strong className="text-foreground">Arquivados</strong>:
                  sai do cardápio e pode ser restaurado quando quiser.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={enviando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={confirmar} disabled={!previa || enviando} data-e2e="excluir-produto-confirmar"
            className={apagar ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}>
            {enviando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : apagar ? <Trash2 className="w-4 h-4 mr-1.5" /> : <Archive className="w-4 h-4 mr-1.5" />}
            {apagar ? "Apagar de vez" : "Arquivar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
